/**
 * @file 处理一个页面同一时刻只播放一个视频/音频的 hook
 * @author hejinfeng01
 */
import {useCallback, useEffect, useRef} from 'react';

// 记录页面内当前正在播放的媒体元素，保证同时只有一个在播放
let currentPlayingMedia: HTMLMediaElement | null = null;

export function useSinglePlayerControl() {
    // 当前已绑定监听的媒体元素
    const boundMediaRef = useRef<HTMLMediaElement | null>(null);
    // 解绑当前监听的函数
    const detachRef = useRef<(() => void) | null>(null);

    // 父级调用 play 时主动调用，传入即将播放的媒体元素，若与上次不同则重绑 pause/ended 监听
    const activatePlayer = useCallback((media: HTMLMediaElement | null) => {
        // 元素被替换时重绑 pause/ended，保证旧元素结束播放后能释放全局变量 currentPlayingMedia
        if (media !== boundMediaRef.current) {
            detachRef.current?.();
            detachRef.current = null;
            boundMediaRef.current = media;

            if (media) {
                // 播放结束或暂停时，若全局变量是当前 media 元素，则置空 currentPlayingMedia
                const handleRelease = () => {
                    if (currentPlayingMedia === media) {
                        currentPlayingMedia = null;
                    }
                };
                media.addEventListener('pause', handleRelease);
                media.addEventListener('ended', handleRelease);
                detachRef.current = () => {
                    media.removeEventListener('pause', handleRelease);
                    media.removeEventListener('ended', handleRelease);
                    if (currentPlayingMedia === media) {
                        currentPlayingMedia = null;
                    }
                };
            }
        }

        if (currentPlayingMedia && currentPlayingMedia !== media) {
            currentPlayingMedia.pause();
        }
        currentPlayingMedia = media;
    }, []);

    useEffect(() => {
        return () => {
            detachRef.current?.();
            detachRef.current = null;
            boundMediaRef.current = null;
        };
    }, []);

    return {
        activatePlayer,
    };
}
